import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import './SampleLoadStatus.scss';

// Above a cached decode so an already-downloaded sound never flickers, below any real download
const PROGRESS_DELAY_MS = 300;

const SampleLoadStatus = ({ status, loaded, total, name, error, retry }) => {
  const { t } = useTranslation();
  const isLoading = status === 'loading';
  const [hasWaited, setHasWaited] = useState(false);

  useEffect(() => {
    if (!isLoading) {
      setHasWaited(false);

      return;
    }

    const timer = setTimeout(() => setHasWaited(true), PROGRESS_DELAY_MS);

    return () => clearTimeout(timer);
  }, [isLoading]);

  if (status === 'error') {
    return (
      <p className="SampleLoadStatus load-error">
        {error}{' '}
        <button type="button" className="retry" onClick={retry}>
          {t('dialogs.sampleLoad.retry')}
        </button>
      </p>
    );
  }

  if (!isLoading || !hasWaited) return null;

  const percent = total ? Math.round((loaded / total) * 100) : 0;

  return (
    <div className="SampleLoadStatus" role="status" aria-live="polite">
      <div className="track">
        <div className="fill" style={{ width: `${percent}%` }} />
      </div>
      <span className="caption">
        {t('dialogs.sampleLoad.downloading', { name })}
        {total ? ` · ${loaded}/${total}` : ''}
      </span>
    </div>
  );
};

SampleLoadStatus.propTypes = {
  status: PropTypes.oneOf(['idle', 'loading', 'ready', 'error']).isRequired,
  loaded: PropTypes.number,
  total: PropTypes.number,
  name: PropTypes.string,
  error: PropTypes.string,
  retry: PropTypes.func.isRequired,
};

export default SampleLoadStatus;
